import { Box, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';
import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DatePicker, LocalizationProvider, PickersDay, PickersDayProps } from '@mui/x-date-pickers-pro';
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon';
import { DateTime } from 'luxon';
import { ReactNode, useMemo } from 'react';
import i18n from '../../i18n';

interface BeltDayNavigatorProps {
  currentDate: Date;
  observedAt?: {
    first: Date;
    last: Date;
  };
  measurementCount?: number;
  daysWithData?: Set<string>;
  onSelectDay: (date: Date) => void;
  onMonthChange?: (date: Date) => void;
}

export default function BeltDayNavigator({
  currentDate,
  observedAt,
  measurementCount,
  daysWithData,
  onSelectDay,
  onMonthChange,
}: BeltDayNavigatorProps) {
  const { t } = useTranslation();

  const pickerValue = useMemo(() => DateTime.fromJSDate(currentDate), [currentDate]);

  const isNextDayDisabled = pickerValue.startOf('day') >= DateTime.now().startOf('day');

  const handleChangeDay = (deltaD: number) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + deltaD);
    onSelectDay(newDate);
  };

  const handleSelectDay = (date: DateTime | null) => {
    if (date) {
      onSelectDay(date.toJSDate());
    }
  };

  const handleMonthChange = (date: DateTime) => {
    onMonthChange?.(date.toJSDate());
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
      <Stack flexDirection='row' justifyContent='center' alignItems='center' gap={2}>
        <NavigationButton icon={<ArrowBackIosNew fontSize='small' />} handleClick={() => handleChangeDay(-1)} />
        <Paper
          variant='outlined'
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr auto 1fr',
            gap: 1,
            borderRadius: 2,
          }}
        >
          <ColumnLayout>
            <Typography variant='overline' color='text.secondary' lineHeight={1} noWrap>
              {t('common.date')}
            </Typography>
            <DatePicker
              value={pickerValue}
              onChange={handleSelectDay}
              onMonthChange={handleMonthChange}
              format='D'
              disableFuture
              slots={{ day: HighlightedDay, actionBar: DaysWithDataLegend }}
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'standard',
                  sx: { '& .MuiPickersInputBase-sectionsContainer': { width: 140, justifyContent: 'center' } },
                },
                day: { daysWithData } as any,
              }}
            />
          </ColumnLayout>
          <Divider orientation='vertical' flexItem />
          <ColumnLayout>
            <Typography variant='overline' color='text.secondary' lineHeight={1} noWrap>
              {t('common.first')}/{t('common.last')}
            </Typography>
            <Typography variant='h6' fontWeight={600} noWrap>
              {observedAt
                ? `${t('common.intlDateTime', {
                    val: observedAt.first.getTime(),
                    formatParams: { val: { timeStyle: 'short' } },
                  })} — ${t('common.intlDateTime', {
                    val: observedAt.last.getTime(),
                    formatParams: { val: { timeStyle: 'short' } },
                  })}`
                : '—'}
            </Typography>
          </ColumnLayout>
          <Divider orientation='vertical' flexItem />
          <ColumnLayout>
            <Typography variant='overline' color='text.secondary' lineHeight={1} noWrap>
              {t('belt.measurements')}
            </Typography>
            <Typography variant='h6' fontWeight={600} noWrap>
              {measurementCount ?? 0}
            </Typography>
          </ColumnLayout>
        </Paper>
        <NavigationButton
          icon={<ArrowForwardIos fontSize='small' />}
          handleClick={() => handleChangeDay(1)}
          disabled={isNextDayDisabled}
        />
      </Stack>
    </LocalizationProvider>
  );
}

function ColumnLayout({ children }: { children: ReactNode }) {
  return (
    <Stack direction='column' alignItems='center' justifyContent='center' gap={0.5} py={2} px={2}>
      {children}
    </Stack>
  );
}

function NavigationButton({
  icon,
  handleClick,
  disabled,
}: {
  icon: ReactNode;
  handleClick: () => void;
  disabled?: boolean;
}) {
  return (
    <IconButton size='large' color='primary' onClick={handleClick} disabled={disabled}>
      {icon}
    </IconButton>
  );
}

function DaysWithDataLegend({ className }: { className?: string }) {
  const { t } = useTranslation();

  return (
    <Stack direction='row' alignItems='center' gap={1} className={className} sx={{ px: 2, paddingBottom: 2 }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: '#eeeeee',
          border: '1px solid',
          borderColor: 'divider',
        }}
      />
      <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 0 }}>
        {t('belt.daysWithDataLegend')}
      </Typography>
    </Stack>
  );
}

interface HighlightedDayProps extends PickersDayProps {
  daysWithData?: Set<string>;
}

function HighlightedDay({ daysWithData, day, outsideCurrentMonth, selected, ...other }: HighlightedDayProps) {
  const hasData = !outsideCurrentMonth && daysWithData?.has(day.toISODate() ?? '');

  return (
    <PickersDay
      {...other}
      day={day}
      selected={selected}
      outsideCurrentMonth={outsideCurrentMonth}
      sx={
        hasData && !selected
          ? {
              backgroundColor: '#eeeeee',
            }
          : undefined
      }
    />
  );
}
